import { helpWhy } from '../../help/why';
import React, { useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    LinearProgress,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { UserBulkImportService } from '../../services/userBulkImport.service';
import { IBulkOnboardingCommitResult, IBulkOnboardingReport } from '../../interfaces/bulk-onboarding.interface';
import { useToast } from '../../context/ToastContext';
import { usePlan } from '../../context/PlanContext';
import UpgradeNudge from '../common/UpgradeNudge';
import { freeTierLimitMessage, isFreeTierLimitError } from '../../utils/planErrors';
import { downloadCsv, sanitizeForSpreadsheet } from '../../utils/csv';

interface Props {
    open: boolean;
    institutionId: string;
    onClose: () => void;
    onImported: () => void;
}

type Step = 'pick' | 'preview' | 'result';

/** Import columns a file column can be read as (2026-10-25 Excel slice D4). `ignore` = not used. */
const COLUMN_TARGETS: { key: string; label: string }[] = [
    { key: 'ignore', label: 'Not used' },
    { key: 'employeecode', label: 'Employee code' },
    { key: 'username', label: 'Username' },
    { key: 'fullname', label: 'Full name' },
    { key: 'firstname', label: 'First name' },
    { key: 'lastname', label: 'Last name' },
    { key: 'designation', label: 'Designation' },
    { key: 'department', label: 'Department' },
    { key: 'joiningdate', label: 'Joining date' },
    { key: 'phone', label: 'Phone' },
    { key: 'email', label: 'Email' },
    { key: 'gender', label: 'Gender' },
    { key: 'role', label: 'Role' },
    { key: 'password', label: 'Password' },
    { key: 'wage_grade', label: 'Wage grade' },
    { key: 'shift_code', label: 'Shift code' },
    { key: 'device_user_id', label: 'Device user ID' },
    { key: 'device_serial', label: 'Device serial' },
    { key: 'org_unit_code', label: 'Org unit code' },
];

function targetOptions(current: string): { key: string; label: string }[] {
    if (COLUMN_TARGETS.some(t => t.key === current)) return COLUMN_TARGETS;
    const label = current.startsWith('salary:') ? `Salary: ${current.slice('salary:'.length)}` : current;
    return [...COLUMN_TARGETS, { key: current, label }];
}

/**
 * Bulk worker onboarding (decision note docs/decisions/2026-09-08-bulk-onboarding.md), D7's UI
 * shape: file picker -> dry-run preview table (per-row status/errors) -> confirm -> summary +
 * credentials-CSV download button (client-side blob from the JSON response).
 *
 * <p>REV2 R3: the commit response is JSON-only and its {@code password} field is populated only
 * for server-generated passwords. The credentials CSV is built entirely here — RFC-4180 quoting
 * comes from {@link downloadCsv} (it always quotes every cell), and formula-prefix sanitization of
 * every operator-supplied cell comes from {@link sanitizeForSpreadsheet}.
 */
export default function BulkOnboardingDialog({ open, institutionId, onClose, onImported }: Props) {
    const toast = useToast();
    const { refresh: refreshPlan } = usePlan();
    const [step, setStep] = useState<Step>('pick');
    const [file, setFile] = useState<File | null>(null);
    const [report, setReport] = useState<IBulkOnboardingReport | null>(null);
    const [result, setResult] = useState<IBulkOnboardingCommitResult | null>(null);
    const [loading, setLoading] = useState(false);
    const [capNudgeMessage, setCapNudgeMessage] = useState<string | null>(null);
    // The user's column choices; only changed columns are sent. `mappingDirty` = changed since the
    // last check, so Confirm waits for a fresh preview of the new reading.
    const [mapping, setMapping] = useState<Record<string, string>>({});
    const [mappingDirty, setMappingDirty] = useState(false);

    const reset = () => {
        setStep('pick');
        setFile(null);
        setMapping({});
        setMappingDirty(false);
        setReport(null);
        setResult(null);
        setCapNudgeMessage(null);
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFile(e.target.files?.[0] ?? null);
        setMapping({});
        setMappingDirty(false);
        setReport(null);
        setCapNudgeMessage(null);
    };

    const handlePreview = async () => {
        if (!file) return;
        setLoading(true);
        try {
            const r = await UserBulkImportService.dryRun(institutionId, file, mapping);
            setReport(r);
            setMappingDirty(false);
            setStep('preview');
        } catch (err) {
            toast.showApiError(err, 'Failed to check the file');
        } finally {
            setLoading(false);
        }
    };

    const handleConfirm = async () => {
        if (!file) return;
        setLoading(true);
        setCapNudgeMessage(null);
        try {
            const r = await UserBulkImportService.commit(institutionId, file, mapping);
            setResult(r);
            setStep('result');
            refreshPlan();
            onImported();
        } catch (err) {
            if (isFreeTierLimitError(err)) {
                setCapNudgeMessage(
                    freeTierLimitMessage(err) ?? 'The free plan is limited to 50 employees. Upgrade to add more.'
                );
            } else {
                toast.showApiError(err, 'Failed to import workers');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadTemplate = () => {
        const headers = [
            'username', 'password', 'firstName', 'lastName', 'designation', 'employeeCode',
            'joiningDate', 'email', 'phone', 'gender', 'role', 'department', 'org_unit_code',
            'shift_code', 'device_user_id', 'device_serial', 'salary:Basic',
        ];
        const rows = [
            ['rahim', '', 'Rahim', 'Uddin', 'Operator', 'EMP-1001', '2026-09-01', '', '01712345678',
                'male', '', 'Sewing', 'LINE-A1', 'GEN', '17', '', '12000'],
        ];
        downloadCsv('bulk-onboarding-template.csv', headers, rows);
    };

    const handleDownloadCredentials = () => {
        if (!result) return;
        const headers = ['username', 'password', 'firstName', 'lastName', 'designation', 'employeeCode'];
        const rows = result.users.map(u => [
            sanitizeForSpreadsheet(u.username),
            u.password ?? '(password was supplied in the source CSV — not shown here)',
            sanitizeForSpreadsheet(u.firstName),
            sanitizeForSpreadsheet(u.lastName),
            sanitizeForSpreadsheet(u.designation),
            sanitizeForSpreadsheet(u.employeeCode),
        ]);
        downloadCsv(`bulk-onboarding-credentials-${institutionId}.csv`, headers, rows);
    };

    const handleColumnChange = (sourceHeader: string, mappedTo: string) => {
        setMapping(prev => ({ ...prev, [sourceHeader]: mappedTo }));
        setMappingDirty(true);
    };

    const columnTarget = (sourceHeader: string, fallback: string) => mapping[sourceHeader] ?? fallback;

    const canConfirm = !!report && !mappingDirty && report.batchErrors.length === 0 && report.errorRows === 0;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth data-help-page="users.bulk.page">
            <DialogTitle>Bulk import workers</DialogTitle>
            <DialogContent>
                {loading && <LinearProgress sx={{ mb: 2 }} />}
                {capNudgeMessage && <UpgradeNudge sx={{ mb: 2 }} message={capNudgeMessage} />}

                {step === 'pick' && (
                    <Box>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            Upload a CSV or Excel (.xlsx, first sheet) file of new workers (max 2,000
                            rows, 5&nbsp;MB). Your own column names are fine (e.g. Emp ID, Card No,
                            Name, DOJ, Mobile, Section, Grade, Punch ID): the preview shows how each
                            column was read and you can change it. Without a username column, the
                            username is made from the employee code. Columns: username,
                            password (optional), firstName, lastName (or one name column),
                            designation, employeeCode,
                            joiningDate (yyyy-MM-dd or dd/MM/yyyy), email, phone, gender (optional — male, female,
                            or other), role (optional), department (optional),
                            org_unit_code (optional — a department/floor/section/line code),
                            shift_code (optional — an existing shift's code; the worker is assigned
                            to it from their joining date), device_user_id (optional, + optional
                            device_serial — the enrolment number already synced from the attendance
                            terminal, mapped to the new account), salary:&lt;ComponentName&gt;
                            (optional), wage_grade (optional — an active wage grade's code or name;
                            needs a salary amount in the same row). Do not include the institution's code prefix in the username
                            column — it is added automatically.
                        </Typography>
                        <Button variant="outlined" size="small" sx={{ mb: 2 }} onClick={handleDownloadTemplate} data-help="users.bulk.template">
                            Download template CSV
                        </Button>
                        <input
                            type="file"
                            accept=".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                            onChange={handleFileChange}
                            data-help="users.bulk.file"
                            data-testid="bulk-onboarding-file-input"
                        />
                    </Box>
                )}

                {step === 'preview' && report && (
                    <Box>
                        <Typography variant="subtitle1" sx={{ mb: 1 }}>
                            {report.validRows} of {report.totalRows} row(s) are ready to import
                            {report.errorRows > 0 ? `, ${report.errorRows} have errors` : ''}
                            {report.warningRows > 0 ? `, ${report.warningRows} have warnings (they can still be imported)` : ''}.
                        </Typography>
                        {(report.columns ?? []).length > 0 && (
                            <Box sx={{ mb: 2 }} data-help-section="users.bulk.columns">
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    How your columns were read
                                </Typography>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell data-help="users.bulk.columns.source">Your column</TableCell>
                                            <TableCell data-help="users.bulk.columns.target">Read as</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {report.columns.map(c => {
                                            const current = columnTarget(c.sourceHeader, c.mappedTo);
                                            return (
                                                <TableRow key={c.sourceHeader}>
                                                    <TableCell>{c.sourceHeader}</TableCell>
                                                    <TableCell>
                                                        <TextField
                                                            select
                                                            size="small"
                                                            fullWidth
                                                            value={current}
                                                            onChange={e => handleColumnChange(c.sourceHeader, e.target.value)}
                                                            inputProps={{ 'aria-label': `Read ${c.sourceHeader} as` }}
                                                            data-help="users.bulk.columns.select"
                                                        >
                                                            {targetOptions(current).map(t => (
                                                                <MenuItem key={t.key} value={t.key}>{t.label}</MenuItem>
                                                            ))}
                                                        </TextField>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                                {mappingDirty && (
                                    <Alert
                                        severity="info"
                                        sx={{ mt: 1 }}
                                        action={
                                            <Button color="inherit" size="small" onClick={handlePreview} disabled={loading} data-help="users.bulk.columns.recheck">
                                                Check again
                                            </Button>
                                        }
                                    >
                                        You changed how a column is read. Check the file again before importing.
                                    </Alert>
                                )}
                            </Box>
                        )}
                        {report.batchErrors.length > 0 && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {report.batchErrors.join(' ')}
                            </Alert>
                        )}
                        {report.freeTierWarning && (
                            <UpgradeNudge sx={{ mb: 2 }} message={report.freeTierWarning.message} />
                        )}
                        <Box sx={{ maxHeight: 360, overflow: 'auto' }} data-help-section="users.bulk.preview">
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        {/* L-3: commons-csv's own data-record numbering (first
                                            data row = 1), one less than the spreadsheet line an
                                            operator would count (line 1 is the header) — labeled
                                            explicitly so "Data row 1" isn't misread as "line 1". */}
                                        <TableCell data-help="users.bulk.col.row">Data row</TableCell>
                                        <TableCell data-help="users.bulk.col.username">Username</TableCell>
                                        <TableCell data-help="users.bulk.col.shift">Shift</TableCell>
                                        <TableCell data-help="users.bulk.col.device">Device user</TableCell>
                                        <TableCell data-help="users.bulk.col.orgUnit">Org unit</TableCell>
                                        <TableCell data-help="users.bulk.col.status">Status</TableCell>
                                        <TableCell data-help="users.bulk.col.errors">Errors</TableCell>
                                        <TableCell data-help="users.bulk.col.warnings">Warnings</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {report.rows.map(row => (
                                        <TableRow key={row.rowNumber}>
                                            <TableCell>{row.rowNumber}</TableCell>
                                            <TableCell>{row.username}</TableCell>
                                            <TableCell>{row.shiftCode ?? ''}</TableCell>
                                            <TableCell>
                                                {row.deviceUserId != null
                                                    ? (row.deviceSerial
                                                        ? `${row.deviceUserId} @ ${row.deviceSerial}`
                                                        : `${row.deviceUserId}`)
                                                    : ''}
                                            </TableCell>
                                            <TableCell>{row.orgUnitCode ?? ''}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    label={row.valid ? 'OK' : 'Error'}
                                                    color={row.valid ? 'success' : 'error'}
                                                />
                                            </TableCell>
                                            <TableCell>{row.errors.join('; ')}</TableCell>
                                            <TableCell>{(row.warnings ?? []).join('; ')}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    </Box>
                )}

                {step === 'result' && result && (
                    <Box>
                        <Alert severity="success" sx={{ mb: 2 }}>
                            {result.created} worker(s) created.
                        </Alert>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            Passwords are shown ONLY in the credentials CSV below, and only this
                            once — they cannot be retrieved again. Download it now, distribute the
                            credentials, then delete the file. Also delete the source CSV you
                            uploaded if it contained any plaintext passwords. Each password works once: the worker must choose their own at first login.
                        </Typography>
                        <Button variant="contained" onClick={handleDownloadCredentials} data-help="users.bulk.credentials">
                            Download credentials CSV
                        </Button>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                {step === 'pick' && (
                    <>
                        <Button onClick={handleClose} data-help="users.bulk.cancel">Cancel</Button>
                        
                        <Button data-help="users.bulk.previewBtn" variant="contained" disabled={!file || loading} onClick={handlePreview} {...helpWhy(loading ? { kind: 'status', message: 'The file is being checked. Wait a moment.' } : !file ? { kind: 'status', message: 'Choose a CSV or Excel file first.' } : null)}>
                            Preview
                        </Button>
                        
                    </>
                )}
                {step === 'preview' && (
                    <>
                        <Button onClick={() => setStep('pick')} data-help="users.bulk.back">Back</Button>
                        
                        <Button data-help="users.bulk.confirm" variant="contained" disabled={!canConfirm || loading} onClick={handleConfirm} {...helpWhy(loading ? { kind: 'status', message: 'The import is running. Wait a moment.' } : mappingDirty ? { kind: 'status', message: 'You changed how a column is read. Press Check again first.' } : !canConfirm && report && report.batchErrors.length > 0 ? { kind: 'status', message: 'The file as a whole has a problem, shown above. Fix the file, then go back and preview again.' } : !canConfirm ? { kind: 'status', message: 'Every row must be OK before you can confirm. Fix the rows marked Error in your file, then go back and preview again.' } : null)}>
                            Confirm import
                        </Button>
                        
                    </>
                )}
                {step === 'result' && (
                    <Button variant="contained" onClick={handleClose} data-help="users.bulk.done">
                        Done
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}
